/**
 * MailboxHealthDot — tiny status indicator near a business label.
 *
 * Renders a small Warning icon when the business (or the caller's whole
 * portfolio, in aggregate mode) has any active mailbox sync / connection
 * issue. Silent (returns null) when there's nothing to show.
 *
 * Wired into the TopBar business switcher (trigger + dropdown rows) and the
 * InboxDeckView column headers so a dead mailbox is visible from any inbox
 * view, not just the full-width banner.
 *
 * Severity tiers:
 *   - reauth  → red WarningOctagon (urgent: token revoked, sync is dead)
 *   - warn    → amber WarningCircle (backend error or stale-but-not-broken)
 */

import { useMemo } from 'react'
import { WarningCircle, WarningOctagon } from '@phosphor-icons/react'
import { cn } from '@/lib/utils'
import { useSyncStatus } from '@/hooks/useSyncStatus'
import type { SyncIssue, SyncIssueKind } from '@/types/api'

type Severity = 'reauth' | 'warn'

interface MailboxHealthDotProps {
  /**
   * Business to check. Pass null/undefined to check across ALL the caller's
   * businesses (used by the "All Businesses" TopBar label).
   */
  businessId?: string | null
  /** Pixel size of the icon — defaults to 12 (matches small chip glyphs). */
  size?: number
  className?: string
}

function severityFromKinds(kinds: SyncIssueKind[]): Severity | null {
  if (kinds.length === 0) return null
  if (kinds.includes('reauth')) return 'reauth'
  return 'warn'
}

function describe(issues: SyncIssue[]): string {
  const reauth = issues.filter((i) => i.kind === 'reauth').length
  const other = issues.length - reauth
  const parts: string[] = []
  if (reauth)
    parts.push(`${reauth} mailbox${reauth === 1 ? '' : 'es'} need reconnection`)
  if (other)
    parts.push(`${other} mailbox${other === 1 ? '' : 'es'} with sync issues`)
  return parts.join(', ')
}

export function MailboxHealthDot({
  businessId,
  size = 12,
  className,
}: MailboxHealthDotProps) {
  const { data } = useSyncStatus()

  const computed = useMemo(() => {
    const businesses = data?.businesses ?? []
    if (businessId) {
      const entry = businesses.find((b) => b.business_id === businessId)
      if (!entry) return null
      const sev = severityFromKinds(entry.issues.map((i) => i.kind))
      if (!sev) return null
      return { severity: sev, title: describe(entry.issues) }
    }
    // Aggregate: roll up every issue across the caller's businesses.
    const all = businesses.flatMap((b) => b.issues)
    const sev = severityFromKinds(all.map((i) => i.kind))
    if (!sev) return null
    return { severity: sev, title: describe(all) }
  }, [data, businessId])

  if (!computed) return null

  const Icon = computed.severity === 'reauth' ? WarningOctagon : WarningCircle
  const colorClass =
    computed.severity === 'reauth' ? 'text-red-600' : 'text-amber-500'

  return (
    <Icon
      weight="fill"
      size={size}
      className={cn('shrink-0', colorClass, className)}
      aria-label={computed.title}
    >
      <title>{computed.title}</title>
    </Icon>
  )
}
